/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, RevokeReleaseResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';

export type RevokeReleaseMutationVariables = {
  releaseId: string;
  tenantIds: string[];
};

export type RevokeReleaseMutationConfig = {
  onSuccess?: (data: RevokeReleaseResponse, variables: RevokeReleaseMutationVariables) => void;
  onError?: (error: unknown, variables: RevokeReleaseMutationVariables) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: RevokeReleaseMutationVariables) => Promise<unknown> | void;
};

/**
 * Hook for revoking (unpublishing) a release from one or more tenants.
 *
 * @param config.onSuccess - Called after successful release revocation
 * @param config.onError - Called if release revocation fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @example
 * ```tsx
 * const { mutate } = useRevokeReleaseMutation({
 *   onSuccess: (data) => console.log('Release revoked:', data),
 *   onError: (error) => console.error('Revocation failed:', error)
 * });
 *
 * // Revoke release from a tenant
 * mutate({ releaseId: 'release-uuid', tenantIds: ['tenant-uuid'] });
 * ```
 */
export function useRevokeReleaseMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: RevokeReleaseMutationConfig = {}) {
  const queryClient = useQueryClient();

  const { mutate, isError, error, isPending } = useMutation<
    RevokeReleaseResponse,
    ApiError,
    RevokeReleaseMutationVariables
  >({
    mutationFn: async ({ releaseId, tenantIds }: RevokeReleaseMutationVariables) => {
      const response = await mgmtPlaneAPIClient.releases.revokeRelease({
        releaseId,
        requestBody: { tenantIds },
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate tenant releases queries to refresh the published status
      queryClient.invalidateQueries({ queryKey: ['tenant-releases'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-releases-by-os'] });
      queryClient.invalidateQueries({ queryKey: ['all-tenant-releases'] });
      onSuccess?.(data, variables);
    },
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isError, error, isPending };
}
